import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  GeodataModel as GeodataModelV0,
  GeodataStatus,
} from '../../../../../../typings/api/skymap/rest/v0/.common';
import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../../utils/async';
import { FeatureFlagsManager } from '../../../../utils/core/feature-flags-manager';
import { isDefined } from '../../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../hooks/use-dialog';
import { useOptionalUserState } from '../../../state/user-state/user-state';
import { Button, ButtonStyled } from '../../button/button';
import { Icon } from '../../icon/icon';
import { InfoBox } from '../../info-box/info-box';
import { Stack } from '../../stack/stack';
import { getGeodataStatusIconText } from '../gcp-page/geodata-status-icon';
import { useBoundaryPolygons } from '../use-boundary-polygons';
import { useGeodata } from '../use-geodata';
import { FilteredPointCloudDialog } from './filtered-point-cloud-dialog';
import { GeodataArtifacts } from './geodata-artifacts';
import { GeodataDetails } from './geodata-details';
import { SelectGeodata } from './select-geodata';
import { TerrainModelDialog } from './terrain-model-dialog';

interface Props {
  selectedItem: GeodataModel;
  items: GeodataModel[];
}

function hasFinished(geodata: GeodataModel) {
  return (
    geodata?.status === 'generate_skyview_data_finished' ||
    geodata?.status === 'generate_tif_finished' ||
    geodata?.status === 'generate_pointcloud_finished'
  );
}

function isReadyForGcpAdjustmentStep(geodata: GeodataModel) {
  return geodata?.status === 'user_move_flags';
}

function isIdle(geodata: GeodataModel) {
  return (
    isReadyForGcpAdjustmentStep(geodata) ||
    hasFinished(geodata) ||
    hasFailed(geodata) ||
    geodata?.status === 'created'
  );
}

function hasFailed(geodata: GeodataModel) {
  return geodata?.status?.toLowerCase().includes('failed');
}

function hasGcpAlignmentFinished(geodata: GeodataModelV0) {
  return !(
    geodata?.status === 'align_cameras' ||
    geodata?.status === 'align_photos_preparing' ||
    geodata?.status === 'match_photos' ||
    geodata?.status === 'optimize_cameras' ||
    geodata?.status === 'align_photos_failed' ||
    geodata?.status === 'align_photos_pending'
  );
}

function useGeodataResourceUrls(geodata?: GeodataModelV0) {
  const [resourceUrls, setResourceUrls] = useState(geodata?.resourceUrls);

  useEffect(() => {
    setResourceUrls(geodata?.resourceUrls);
    iiafe(async () => {
      const extraItems = [];

      if (!isDefined(geodata)) {
        setResourceUrls([]);
        return;
      }

      if (
        FeatureFlagsManager.instance.isEnabled('SK-5825-cloudprocessing-download-project-zip') &&
        hasGcpAlignmentFinished(geodata)
      ) {
        const response = await SkyMapAxiosServiceFactory.instance
          .createGeodataServiceV0()
          .fetchAgisoftProjectURL({
            path: {
              geodataId: geodata.id,
            },
          });

        extraItems.push({
          name: 'project',
          url: response.url,
        });
      }

      // This makes the GCP file link appear among the artifacts.
      if (
        !geodata.isLegacyProcess &&
        geodata.gcpCollection &&
        geodata.gcpCollection.points.length > 0
      ) {
        extraItems.push({
          name: 'cameras',
          url: '',
        });
      }

      setResourceUrls([...(geodata.resourceUrls ?? []), ...extraItems]);
    });
  }, [geodata]);

  return { resourceUrls };
}

const GeodataInfo = (props: Props) => {
  const { t } = useTranslation('cloudProcessing');
  const userState = useOptionalUserState();
  const isOrganizationAdmin = userState?.isOrganizationAdmin ?? false;
  const terrainModelDialog = useDialog();
  const filteredPointCloudDialog = useDialog();
  const [isReportExporting, setIsReportExporting] = useState(false);

  const { geodata } = useGeodata(props.selectedItem.id);
  const { resourceUrls } = useGeodataResourceUrls(geodata);
  const { boundaryPolygons, isPending: isBoundaryPolygonsPending } = useBoundaryPolygons(
    props.selectedItem.id,
    { enabled: !props.selectedItem.isLegacyProcess },
  );

  return (
    <Component>
      <Title className="notranslate">{props.selectedItem.name}</Title>
      <Stack alignItems="flex-start" direction="row" flexWrap="wrap" spacing="8px">
        {!props.selectedItem.isLegacyProcess && (
          <ActionButton
            leftIcon={{ icon: ['fad', 'file-pdf'] }}
            loading={isReportExporting}
            variant="contained"
            onClick={() => {
              void (async () => {
                try {
                  setIsReportExporting(true);
                  const result = await SkyMapAxiosServiceFactory.instance
                    .createGeodataServiceV0()
                    .createReport({
                      path: {
                        geodataId: props.selectedItem.id,
                      },
                    });

                  window.location.assign(result.url);
                } catch {
                  publish(SubscriptionTopic.ToastrMessage, {
                    type: 'error',
                    message: t('geodataPage.list.reportExportError', { ns: 'cloudProcessing' }),
                  });
                } finally {
                  setIsReportExporting(false);
                }
              })();
            }}
          >
            {t('geodataPage.actions.exportReport', { ns: 'cloudProcessing' })}
          </ActionButton>
        )}
        {isOrganizationAdmin && (
          <ActionButton
            leftIcon={{ icon: ['fad', 'cog'] }}
            variant="contained"
            onClick={() => terrainModelDialog.show()}
          >
            {t('geodataPage.actions.terrainModel', { ns: 'cloudProcessing' })}
          </ActionButton>
        )}
        {isOrganizationAdmin && (
          <ActionButton
            leftIcon={{ icon: ['fad', 'cog'] }}
            variant="contained"
            onClick={() => filteredPointCloudDialog.show()}
          >
            {t('geodataPage.actions.filteredPointCloud', { ns: 'cloudProcessing' })}
          </ActionButton>
        )}
      </Stack>

      {!isIdle(props.selectedItem) && (
        <InfoBox
          bottomMargin={true}
          color="yellow"
          leftIcon={{ icon: ['fad', 'info'] }}
          topMargin={true}
          width="fit-content"
        >
          <Icon icon="spinner" spin={true} />
          <Text>
            {props.selectedItem?.status
              ? getGeodataStatusIconText(props.selectedItem.status as GeodataStatus, t)
              : t('geodataPage.list.processing', { ns: 'cloudProcessing' })}
          </Text>
        </InfoBox>
      )}

      {hasFailed(props.selectedItem) && (
        <InfoBox
          bottomMargin={true}
          color="red"
          leftIcon={{ icon: ['fad', 'info'] }}
          topMargin={true}
          width="fit-content"
        >
          {getGeodataStatusIconText(props.selectedItem.status as GeodataStatus, t)}
          <br />
          <br />
          {hasGcpAlignmentFinished(props.selectedItem) ? (
            <>{t('geodataPage.list.failure.contactSupport', { ns: 'cloudProcessing' })}</>
          ) : (
            <>
              {t('geodataPage.list.failure.verifyData', { ns: 'cloudProcessing' })}
              <br />
              {t('geodataPage.list.failure.contactSupportAgain', { ns: 'cloudProcessing' })}
            </>
          )}
        </InfoBox>
      )}

      <Content>
        <GeodataDetails
          boundaryPolygonCount={boundaryPolygons.length}
          isBoundaryPolygonsPending={isBoundaryPolygonsPending}
          loadedItem={geodata}
          selectedItem={props.selectedItem}
        />
        {geodata && <GeodataArtifacts resourceUrls={resourceUrls} selectedItem={geodata} />}
      </Content>

      {geodata &&
        terrainModelDialog.render(
          <TerrainModelDialog dialog={terrainModelDialog} geodata={geodata} />,
        )}
      {geodata &&
        filteredPointCloudDialog.render(
          <FilteredPointCloudDialog dialog={filteredPointCloudDialog} geodata={geodata} />,
        )}
    </Component>
  );
};

export const GeodataListContent = (props: Partial<Props>) => {
  if (!isDefined(props.selectedItem) || !isDefined(props.items)) {
    return (
      <Component>
        <SelectGeodata />
      </Component>
    );
  }

  return <GeodataInfo items={props.items} selectedItem={props.selectedItem} />;
};

const Component = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  height: 100%;
`;

const ActionButton = styled(Button)`
  flex: 1 1 200px;
  min-width: 200px;
  width: 100%;
`;

const Title = styled.h1`
  font-weight: 500;
  font-size: 1.2em;
`;

const Text = styled.label`
  margin-left: 0.5em;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  margin-top: 1em;
  width: 100%;

  ${ButtonStyled} {
    align-self: flex-start;
    margin-top: 1em;
  }
`;
